import { useRef, useState, useEffect, useCallback } from 'react'
import { messagesApi } from '../../api/messages'
import { mediaApi } from '../../api/media'
import { useChatStore } from '../../stores/chatStore'
import { useUiStore } from '../../stores/uiStore'

interface Props { chatId: string }

const MAX_DURATION = 60  // seconds
const HOLD_DELAY = 220   // ms — below this it's a tap
const LOCK_PX = 100      // vertical drag needed to lock recording

type Phase = 'idle' | 'holding' | 'locked'
type RecMode = 'voice' | 'video'

export function RecordButton({ chatId }: Props) {
  const [mode, setMode] = useState<RecMode>('voice')
  const [phase, setPhase] = useState<Phase>('idle')
  const [duration, setDuration] = useState(0)
  const [lockProgress, setLockProgress] = useState(0) // 0..1

  // Refs for use in event-handlers and timers (avoid stale closures)
  const modeRef = useRef<RecMode>('voice')
  const phaseRef = useRef<Phase>('idle')
  const btnRef = useRef<HTMLButtonElement>(null)
  const videoPreviewRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const durationTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isHoldRef = useRef(false)
  const startYRef = useRef(0)
  const pointerIdRef = useRef(-1)

  const { appendMessage } = useChatStore()
  const { addToast } = useUiStore()

  const setPhaseSync = (p: Phase) => { phaseRef.current = p; setPhase(p) }
  const setModeSync  = (m: RecMode) => { modeRef.current = m; setMode(m) }

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach(t => t.stop())
    streamRef.current = null
  }, [])

  // ── Send ──────────────────────────────────────────────────────────────────
  const doSend = useCallback(async () => {
    const recorder = recorderRef.current
    if (!recorder) return
    if (durationTimerRef.current) { clearInterval(durationTimerRef.current); durationTimerRef.current = null }

    const curMode = modeRef.current
    const mimeType = curMode === 'voice' ? 'audio/webm' : 'video/webm'

    recorder.onstop = async () => {
      stopStream()
      setPhaseSync('idle')
      setDuration(0)
      setLockProgress(0)
      const blob = new Blob(chunksRef.current, { type: mimeType })
      const name = curMode === 'voice' ? `voice_${Date.now()}.webm` : `video_${Date.now()}.webm`
      try {
        const uploaded = await mediaApi.upload(new File([blob], name, { type: mimeType }))
        const msg = await messagesApi.send(chatId, { text: '', media_ids: [uploaded.id] })
        appendMessage(msg)
      } catch {
        addToast('error', 'Не удалось отправить')
      }
    }
    recorder.stop()
    recorderRef.current = null
  }, [chatId, appendMessage, addToast, stopStream])

  // ── Cancel ────────────────────────────────────────────────────────────────
  const doCancel = useCallback(() => {
    const recorder = recorderRef.current
    if (recorder) {
      recorder.ondataavailable = null
      recorder.onstop = null
      recorder.stop()
      recorderRef.current = null
    }
    if (durationTimerRef.current) { clearInterval(durationTimerRef.current); durationTimerRef.current = null }
    chunksRef.current = []
    stopStream()
    setPhaseSync('idle')
    setDuration(0)
    setLockProgress(0)
  }, [stopStream])

  // Keep stable refs for timers
  const doSendRef = useRef(doSend)
  const doCancelRef = useRef(doCancel)
  useEffect(() => { doSendRef.current = doSend }, [doSend])
  useEffect(() => { doCancelRef.current = doCancel }, [doCancel])

  useEffect(() => () => {
    if (holdTimerRef.current) clearTimeout(holdTimerRef.current)
    doCancelRef.current()
  }, [])

  // ── Start recording ───────────────────────────────────────────────────────
  const startRecording = useCallback(async () => {
    const curMode = modeRef.current
    try {
      const constraints = curMode === 'voice'
        ? { audio: true }
        : { video: { facingMode: 'user', width: 480, height: 480 }, audio: true }
      const stream = await navigator.mediaDevices.getUserMedia(constraints)
      streamRef.current = stream

      if (curMode === 'video' && videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream
        videoPreviewRef.current.play().catch(() => {})
      }

      chunksRef.current = []
      const mimeType = curMode === 'voice'
        ? (MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm')
        : (MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus') ? 'video/webm;codecs=vp9,opus' : 'video/webm')

      const recorder = new MediaRecorder(stream, { mimeType })
      recorder.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data) }
      recorder.start(250)
      recorderRef.current = recorder

      setDuration(0)
      durationTimerRef.current = setInterval(() => {
        setDuration(d => {
          if (d + 1 >= MAX_DURATION) { doSendRef.current(); return d }
          return d + 1
        })
      }, 1000)
    } catch {
      addToast('error', curMode === 'voice' ? 'Нет доступа к микрофону' : 'Нет доступа к камере')
      setPhaseSync('idle')
    }
  }, [addToast])

  // ── Pointer handlers ──────────────────────────────────────────────────────
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault()
    pointerIdRef.current = e.pointerId
    btnRef.current?.setPointerCapture(e.pointerId)
    startYRef.current = e.clientY
    isHoldRef.current = false

    holdTimerRef.current = setTimeout(() => {
      isHoldRef.current = true
      setPhaseSync('holding')
      startRecording()
    }, HOLD_DELAY)
  }, [startRecording])

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isHoldRef.current || phaseRef.current !== 'holding') return
    const dy = startYRef.current - e.clientY // positive = dragged up
    if (dy > 0) {
      const progress = Math.min(1, dy / LOCK_PX)
      setLockProgress(progress)
      if (progress >= 1) {
        setPhaseSync('locked')
        setLockProgress(1)
        btnRef.current?.releasePointerCapture(pointerIdRef.current)
      }
    } else {
      setLockProgress(0)
    }
  }, [])

  const handlePointerUp = useCallback(() => {
    if (holdTimerRef.current) { clearTimeout(holdTimerRef.current); holdTimerRef.current = null }
    if (!isHoldRef.current) {
      // Single tap → toggle mode
      setModeSync(modeRef.current === 'voice' ? 'video' : 'voice')
      return
    }
    isHoldRef.current = false
    if (phaseRef.current === 'holding') doSendRef.current()
    // If locked, user uses explicit send/cancel buttons
  }, [])

  const handlePointerCancel = useCallback(() => {
    if (holdTimerRef.current) { clearTimeout(holdTimerRef.current); holdTimerRef.current = null }
    isHoldRef.current = false
    if (phaseRef.current !== 'idle') doCancelRef.current()
  }, [])

  const isRecording = phase === 'holding' || phase === 'locked'
  const circumference = 2 * Math.PI * 95
  const dashOffset = circumference * (1 - duration / MAX_DURATION)
  const fmtDuration = `${Math.floor(duration / 60)}:${(duration % 60).toString().padStart(2, '0')}`

  return (
    <div className="relative flex items-center justify-center shrink-0">
      {/* ── Video circle preview ─────────────────────────────────────────── */}
      {isRecording && mode === 'video' && (
        <div className="absolute bottom-full right-0 mb-3 w-32 h-32 rounded-full overflow-hidden shadow-2xl ring-2 ring-red-500">
          <svg className="absolute inset-0 w-full h-full z-10 -rotate-90" viewBox="0 0 200 200">
            <circle cx="100" cy="100" r="95" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="8"/>
            <circle
              cx="100" cy="100" r="95" fill="none" stroke="#ef4444" strokeWidth="8"
              strokeDasharray={circumference} strokeDashoffset={dashOffset}
              strokeLinecap="round" className="transition-all duration-1000"
            />
          </svg>
          <video
            ref={videoPreviewRef}
            muted playsInline autoPlay
            className="w-full h-full object-cover"
            style={{ transform: 'scaleX(-1)' }}
          />
        </div>
      )}

      {/* ── Duration counter (left of button, visible while recording) ──── */}
      {isRecording && (
        <span className="absolute right-full mr-2 text-xs font-mono text-txt-primary whitespace-nowrap flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse inline-block" />
          {fmtDuration}
        </span>
      )}

      {/* ── Lock indicator (appears above button while dragging up) ──────── */}
      {phase === 'holding' && lockProgress > 0 && (
        <div
          className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 pointer-events-none"
          style={{ opacity: Math.min(1, lockProgress * 2) }}
        >
          {/* upward chevron */}
          <svg width="10" height="12" viewBox="0 0 10 12" fill="none" className="text-txt-meta">
            <path d="M5 10V2M2 5l3-3 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {/* lock icon */}
          <div
            className="w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all"
            style={{
              transform: `scale(${0.6 + 0.4 * lockProgress})`,
              backgroundColor: lockProgress >= 1 ? 'var(--accent)' : 'var(--bg-input)',
              borderColor: lockProgress >= 1 ? 'var(--accent)' : 'rgba(128,128,128,0.4)',
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                 stroke={lockProgress >= 1 ? 'white' : 'currentColor'}
                 strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                 className="text-txt-meta">
              <rect x="3" y="11" width="18" height="11" rx="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
        </div>
      )}

      {/* ── Locked state: Send + Cancel above button ─────────────────────── */}
      {phase === 'locked' && (
        <div className="absolute bottom-full mb-2 right-0 flex flex-col items-center gap-1.5">
          {/* Send */}
          <button
            onClick={doSend}
            className="w-9 h-9 rounded-full bg-accent flex items-center justify-center text-white shadow-lg hover:bg-blue-500 transition-colors"
            title="Отправить"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
            </svg>
          </button>
          {/* Lock pill (locked icon) */}
          <div className="w-8 h-8 rounded-full bg-accent/20 border border-accent/40 flex items-center justify-center">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-accent">
              <rect x="3" y="11" width="18" height="11" rx="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          {/* Cancel */}
          <button
            onClick={doCancel}
            className="w-8 h-8 rounded-full bg-bg-input border border-divider flex items-center justify-center text-txt-secondary hover:text-red-400 transition-colors shadow-md"
            title="Отменить"
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      )}

      {/* ── Main button ───────────────────────────────────────────────────── */}
      <button
        ref={btnRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        className={`w-9 h-9 flex items-center justify-center rounded-full text-white transition-all select-none touch-none
          ${isRecording
            ? 'bg-red-500 scale-110 shadow-lg shadow-red-500/30'
            : 'bg-accent hover:bg-blue-500'}`}
        title={
          phase === 'locked'
            ? 'Запись зафиксирована'
            : isRecording
              ? 'Запись... потяни вверх чтобы зафиксировать'
              : mode === 'voice'
                ? 'Голосовое • тап = видеокружок • держать = запись'
                : 'Видеокружок • тап = голосовое • держать = запись'
        }
      >
        {isRecording ? (
          <span className="w-2.5 h-2.5 rounded-full bg-white animate-pulse" />
        ) : mode === 'voice' ? (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
            <line x1="12" y1="19" x2="12" y2="23"/>
            <line x1="8" y1="23" x2="16" y2="23"/>
          </svg>
        ) : (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none"/>
          </svg>
        )}
      </button>
    </div>
  )
}
